import { AuthData, AuthConfig, UserProfile, AccountSelection } from './types';
import { API_BASE } from './types';
import { extractUserProfile, b64toBlob } from './helpers';

/**
 * Extract a user-friendly error message from ABDM API error responses.
 *
 * The backend may return abdmError as either:
 *   - A parsed object:  { "loginId": "Invalid LoginId", "timestamp": "..." }
 *   - A JSON string:    "{\"loginId\":\"Invalid LoginId\",\"timestamp\":\"...\"}"
 *
 * The error fields inside abdmError are domain-specific (e.g. "loginId", "otp",
 * "mobile") rather than a generic "code"/"message" shape. We extract all
 * non-timestamp fields and join them into a readable message.
 */
const extractAbdmError = (data: any, status: number): string => {
    if (data.abdmError) {
        let abdmErrorObj: Record<string, any> | null = null;

        if (typeof data.abdmError === 'string') {
            try {
                abdmErrorObj = JSON.parse(data.abdmError);
            } catch {
                // If it's a plain string (not JSON), use it directly
                if (data.abdmError.toLowerCase().includes('user not found')) {
                    return 'User not found';
                }
                return `ABDM Error: ${data.abdmError}`;
            }
        } else if (typeof data.abdmError === 'object') {
            abdmErrorObj = data.abdmError;
        }

        if (abdmErrorObj) {
            // If it has the standard code/message shape, use that
            if (abdmErrorObj.code && abdmErrorObj.message) {
                // For "User not found" errors, show only the clean message
                const msg = abdmErrorObj.message as string;
                if (msg.toLowerCase().includes('user not found')) {
                    return 'User not found';
                }
                return `ABDM Error: ${msg} (${abdmErrorObj.code})`;
            }

            // Otherwise collect all meaningful field-level errors (skip timestamp/status)
            const skipKeys = new Set(['timestamp', 'status']);
            const errorParts: string[] = [];
            for (const [key, value] of Object.entries(abdmErrorObj)) {
                if (!skipKeys.has(key) && value && typeof value === 'string') {
                    errorParts.push(value);
                }
            }
            if (errorParts.length > 0) {
                const joined = errorParts.join('; ');
                if (joined.toLowerCase().includes('user not found')) {
                    return 'User not found';
                }
                return `ABDM Error: ${joined}`;
            }
        }
    }

    // Fallback: also try parsing data.error if it's a JSON string with nested abdmError
    if (data.error && typeof data.error === 'string') {
        try {
            const parsedError = JSON.parse(data.error);
            if (parsedError.abdmError) {
                // Recursively extract from the nested structure
                return extractAbdmError(parsedError, status);
            }
            if (parsedError.message) {
                return parsedError.message;
            }
        } catch {
            // Not JSON — ignore
        }
    }

    return data.message || `Request failed with status ${status}`;
};

export const handleLogin = async (loginData: { username: string; password: string }): Promise<AuthData> => {
    // V2 auth endpoint — uses email (not username) and returns accessToken
    const response = await fetch(`${API_BASE}/api/users/v2/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginData.username, password: loginData.password }),
    });

    const data = await response.json();

    if (!response.ok || !data.accessToken) {
        throw new Error(data.message || `Login failed with status ${response.status}`);
    }

    const user = data.user || {};
    const tenantId = user.branchId || user.organizationId || '';

    // Read callbackUrl directly from login response (added to StaffLoginController
    // backend on 2026-05-30 — see hotfix in HealthProPlusAuthenticationService).
    // Fall back to the legacy /api/tenant/getTenantByTenantId only if the field
    // is absent (e.g. against older backend builds).
    let callbackUrl: string | undefined = user.callbackUrl || undefined;
    if (!callbackUrl && tenantId) {
        try {
            const tenantResp = await fetch(`${API_BASE}/api/tenant/getTenantByTenantId?tenantId=${tenantId}`, {
                headers: { 'Authorization': `Bearer ${data.accessToken}` },
            });
            if (tenantResp.ok) {
                const tenantData = await tenantResp.json();
                callbackUrl = tenantData.callbackUrl || undefined;
            }
        } catch {
            console.warn('Could not fetch tenant callbackUrl, will skip client-side callback');
        }
    }

    return {
        token: data.accessToken,
        tenantId,
        organizationName: user.organizationName || '',
        organizationType: user.roleAssignments?.[0]?.roleKey || 'ADMIN',
        role: user.roleAssignments?.[0]?.roleKey || 'ADMIN',
        branchId: user.branchId,
        staffName: user.fullName,
        facilityPhone: user.facilityPhone,
        facilityEmail: user.facilityEmail,
        facilityAddress: user.facilityAddress,
        callbackUrl,
    };
};

export interface RequestOTPResult {
    txnId: string;
    maskedMobile?: string; // e.g. "******2159" — extracted from ABDM message like "ending with ******2159"
}

export const handleRequestOTP = async (
    authData: AuthData,
    authConfig: AuthConfig,
    existingTxnId?: string,   // pass current enrollment txnId so ABDM links mobile OTP to the session
    xToken?: string           // pass xToken for enrollment mobile OTP request if available
): Promise<RequestOTPResult> => {
    if (!authConfig.value) {
        throw new Error('Please fill in all required fields.');
    }

    let requestValue = authConfig.value;
    if (authConfig.type === 'abha-address' && !requestValue.includes('@')) {
        requestValue = `${requestValue}@sbx`;
    }

    const requestBody: any = {
        tenantId: authData.tenantId,
        type: authConfig.type,
        method: authConfig.method,
        value: requestValue,
        // Use existing txnId when continuing an enrollment session (e.g. mobile OTP step),
        // otherwise empty string for fresh OTP requests.
        txnId: existingTxnId || ""
    };

    // Pass enrollmentMode so backend can add 'dl-flow' to scope for DL enrollment
    if (authConfig.enrollmentMode) {
        requestBody.enrollmentMode = authConfig.enrollmentMode;
    }

    // Pass xToken for enrollment mobile OTP so backend can set X-Token header if needed
    if (xToken) {
        requestBody.xToken = xToken;
    }

    const response = await fetch(`${API_BASE}/api/abdm/v3/auth/request/otp`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authData.token}`
        },
        body: JSON.stringify(requestBody),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(extractAbdmError(data, response.status));
    }

    const finalTxnId = data.txnId || data.data?.txnId;
    if (!finalTxnId) {
        throw new Error("OTP sent but no transaction ID received from gateway.");
    }

    // Extract masked mobile from ABDM message e.g. "OTP sent to Aadhaar registered mobile number ending with ******2159"
    let maskedMobile: string | undefined;
    const msgs = [data.message, data.data?.message].filter(Boolean);
    for (const msg of msgs) {
        const match = msg.match(/(\*+\d+)\s*$/);
        if (match) {
            maskedMobile = match[1]; // e.g. "******2159"
            break;
        }
    }

    return { txnId: finalTxnId, maskedMobile };
};

export interface VerifyOTPResult {
    xToken?: string;
    tToken?: string;
    accounts?: AccountSelection[];
    profile?: UserProfile;
    isEnrollment: boolean;
    txnId?: string; // ABDM returns a NEW txnId after verification — must replace the old one
}

export const handleVerifyOTP = async (
    authData: AuthData,
    authConfig: AuthConfig,
    transactionId: string,
    otpInput: string,
    xTokenOverride?: string   // pass xToken for enrollment+mobile verify so backend sets X-Token header
): Promise<VerifyOTPResult> => {
    if (!transactionId || otpInput.length !== 6) {
        throw new Error('Missing OTP or transaction ID.');
    }

    let mobileValue = '';
    if (authConfig.type === 'enrollment' && authConfig.method === 'aadhaar') {
        mobileValue = authConfig.aadhaarMobileInput;
    } else if (authConfig.type === 'communication-mobile' || authConfig.type === 'communication-mobile-user') {
        mobileValue = authConfig.value;
    } else if (authConfig.method === 'mobile' && /^\d{10}$/.test(authConfig.value)) {
        // Only pass as mobile if it's 10 digits (to avoid passing ABHA address here)
        mobileValue = authConfig.value;
    }

    let requestValue = authConfig.value;
    if (authConfig.type === 'abha-address' && !requestValue.includes('@')) {
        requestValue = `${requestValue}@sbx`;
    }

    const requestBody: any = {
        tenantId: authData.tenantId,
        type: authConfig.type,
        method: authConfig.method,
        txnId: transactionId,
        otp: otpInput,
        value: requestValue // 👈 Critical: Pass the identifier (ABHA No/Address) back for verification
    };

    if (mobileValue) {
        requestBody.mobile = mobileValue;
    }

    // For enrollment+mobile verify (/v3/enrollment/auth/byAbdm), pass the existing xToken
    // so the backend can set X-Token header — ABDM needs it to link new mobile to the session.
    if (authConfig.type === 'enrollment' && authConfig.method === 'mobile' && xTokenOverride) {
        requestBody.xToken = xTokenOverride;
    }

    // Pass enrollmentMode so backend can add 'dl-flow' to scope for DL enrollment
    if (authConfig.enrollmentMode) {
        requestBody.enrollmentMode = authConfig.enrollmentMode;
    }

    const response = await fetch(`${API_BASE}/api/abdm/v3/auth/verify/otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify(requestBody),
    });

    const data = await response.json();
    console.log('[ABDM API] Verify OTP Result Raw Data:', JSON.stringify(data, null, 2));
    console.log('[ABDM API] Verify OTP - data.data keys:', data.data ? Object.keys(data.data) : 'NO data.data');
    console.log('[ABDM API] Verify OTP - data.data.tokens:', data.data?.tokens);
    console.log('[ABDM API] Verify OTP - data.data.token:', data.data?.token);

    if (!response.ok) {
        throw new Error(extractAbdmError(data, response.status));
    }

    // Check for ABDM errors returned with HTTP 200 (e.g., invalid OTP)
    if (data.abdmError || (data.status === false && data.error)) {
        const errMsg = extractAbdmError(data, response.status);
        // Detect common OTP errors and give user-friendly message
        const lowerErr = errMsg.toLowerCase();
        if (lowerErr.includes('otp') || lowerErr.includes('invalid') || lowerErr.includes('expired') || lowerErr.includes('incorrect')) {
            throw new Error('Invalid OTP. Please enter a valid OTP.');
        }
        throw new Error(errMsg);
    }

    // For communication-mobile flow, the token is in data.data.token
    let verifiedXToken = data.data?.token || data.data?.tokens?.token || data.xToken || data.token;

    // For communication-mobile flow, tToken is the same as the token from verify OTP response
    let tToken = data.tToken ||
        data.data?.tToken ||
        data.data?.tokens?.tToken ||
        data.tokens?.tToken;

    // Special case for communication-mobile where the token returned IS the tToken for the next step
    if (authConfig.type === 'communication-mobile' && !tToken && verifiedXToken) {
        tToken = verifiedXToken;
    }

    // Fallback: Search the entire data object for any field containing 'token' (case insensitive)
    if (!tToken || !verifiedXToken) {
        const findToken = (obj: any): string | null => {
            if (!obj || typeof obj !== 'object') return null;
            for (const key in obj) {
                if (key.toLowerCase().includes('token') && typeof obj[key] === 'string') return obj[key];
                const found = findToken(obj[key]);
                if (found) return found;
            }
            return null;
        };
        if (!verifiedXToken) verifiedXToken = findToken(data) || undefined;
        if (!tToken && authConfig.type === 'communication-mobile') tToken = verifiedXToken;
    }

    console.log('[ABDM API] Verified Tokens:', { xToken: !!verifiedXToken, tToken: !!tToken, flow: authConfig.type });

    console.log('OTP Verification - xToken:', verifiedXToken ? 'Present' : 'Missing');
    console.log('OTP Verification - tToken:', tToken ? 'Present' : 'Missing');

    const dataContainer = data.data || data;

    // Extract updated txnId — ABDM returns a NEW txnId after OTP verification
    const updatedTxnId = dataContainer.txnId || data.txnId;
    if (updatedTxnId) {
        console.log('[ABDM API] Updated txnId from verify response:', updatedTxnId);
    }

    // Handle Multiple Accounts (Mobile Login Flows)
    const accountList = dataContainer.accounts || data.accounts || [];
    const isCommunicationMobile = authConfig.type === 'communication-mobile' || authConfig.type === 'communication-mobile-user';
    const hasAccounts = accountList && accountList.length > 0;
    const isMobileFlow = authConfig.method === 'mobile' && authConfig.type !== 'enrollment';

    // Communication-mobile flow: no accounts means no ABHA linked to this mobile
    if (isCommunicationMobile && !hasAccounts) {
        throw new Error('NO_ABHA_ON_MOBILE::The mobile number you have entered does not match with any of the records. Please enter a different number.');
    }

    if (hasAccounts && isMobileFlow && (accountList.length > 1 || isCommunicationMobile)) {
        console.log(`Mobile flow: ${accountList.length} accounts found. Redirecting to selection.`);
        return {
            accounts: accountList,
            isEnrollment: false,
            tToken: tToken || undefined,
            xToken: verifiedXToken
        };
    }

    // Handle Enrollment — return updated txnId so subsequent calls use the correct one
    if (authConfig.type === 'enrollment') {
        console.log('[ABDM API] 🔑 Enrollment flow complete. xToken:', verifiedXToken ? `Present (length: ${verifiedXToken.length})` : '❌ MISSING - DL enrollment will get 401!');

        // For existing users (isNew === false OR message says "already exist"), extract profile from ABHAProfile so
        // OTPVerifier can show "ABHA Account Exists" popup (Use Case 2)
        let enrollmentProfile: UserProfile | undefined = undefined;
        const abhaProfile = dataContainer.ABHAProfile || dataContainer.abhaProfile;
        const messageText = (dataContainer.message || data.message || '').toLowerCase();
        const isExistingAccount = dataContainer.isNew === false || messageText.includes('already exist') || messageText.includes('account exist');
        // Also detect by presence of ABHAProfile with preferredAbhaAddress — if ABDM returns a profile, the account exists
        const hasExistingAbhaAddress = abhaProfile && (abhaProfile.preferredAbhaAddress || abhaProfile.preferredAddress || abhaProfile.phrAddress?.length > 0);
        console.log('[ABDM API] Enrollment detect:', {
            isNew: dataContainer.isNew,
            message: messageText.substring(0, 80),
            hasABHAProfile: !!abhaProfile,
            isExistingAccount,
            hasExistingAbhaAddress: !!hasExistingAbhaAddress,
        });
        if (abhaProfile && (isExistingAccount || hasExistingAbhaAddress)) {
            console.log('[ABDM API] Existing ABHA user detected — extracting profile from ABHAProfile');
            const composedName = [abhaProfile.firstName, abhaProfile.middleName, abhaProfile.lastName].filter(Boolean).join(' ');
            const preferredAddr = abhaProfile.preferredAddress || abhaProfile.preferredAbhaAddress || '';
            enrollmentProfile = {
                fullName: composedName || abhaProfile.name || '',
                abhaNumber: abhaProfile.ABHANumber || '',
                abhaAddress: preferredAddr || abhaProfile.abhaAddress || '',
                status: abhaProfile.abhaStatus || 'ACTIVE',
                kycStatus: 'VERIFIED',
                mobileVerified: abhaProfile.mobileVerified || true,
                mobile: abhaProfile.mobile || '',
                address: abhaProfile.address || '',
                profilePhoto: abhaProfile.photo || undefined,
                dob: abhaProfile.dob || '',
                gender: abhaProfile.gender || '',
                email: abhaProfile.email || '',
                district: abhaProfile.districtName || '',
                state: abhaProfile.stateName || '',
            } as UserProfile;
            // Store preferredAbhaAddress as extra field for ABHACreator detection
            (enrollmentProfile as any).preferredAbhaAddress = preferredAddr;
            (enrollmentProfile as any).phrAddress = abhaProfile.phrAddress || [];
        } else if (!abhaProfile && (isExistingAccount || messageText.includes('already')) && verifiedXToken) {
            // ABHAProfile missing but account exists — extract minimal info from JWT token
            console.log('[ABDM API] Existing account but no ABHAProfile — extracting from JWT token');
            try {
                const tokenParts = verifiedXToken.split('.');
                if (tokenParts.length >= 2) {
                    const payload = JSON.parse(atob(tokenParts[1].replace(/-/g, '+').replace(/_/g, '/')));
                    enrollmentProfile = {
                        fullName: payload.name || payload.preferred_username || 'Name on Aadhaar',
                        abhaNumber: payload.abhaNumber || payload.sub || '',
                        abhaAddress: payload.preferredAbhaAddress || payload.phrAddress?.[0] || '',
                        status: 'ACTIVE',
                        kycStatus: payload.isKycVerified ? 'VERIFIED' : 'PENDING',
                        mobileVerified: true,
                        mobile: payload.mobile || '',
                    } as UserProfile;
                    (enrollmentProfile as any).preferredAbhaAddress = payload.preferredAbhaAddress || '';
                    (enrollmentProfile as any).phrAddress = payload.phrAddress || [];
                }
            } catch (e) {
                console.warn('[ABDM API] Failed to decode JWT for existing account info:', e);
            }
        }

        return { xToken: verifiedXToken, isEnrollment: true, txnId: updatedTxnId, profile: enrollmentProfile };
    }

    // Standard Flows (ABHA Number / ABHA Address):
    // Proceed directly to profile extraction without account selection
    let profile = extractUserProfile(dataContainer);

    // Auto-fetch full profile to ensure all fields are populated
    let profileFetchFailed = false;
    if (verifiedXToken && (authConfig.type === 'abha-number' || authConfig.type === 'abha-address' || authConfig.type === 'aadhaar-login')) {
        try {
            console.log('Auto-fetching full profile in OTPVerifier...');
            const fullProfile = await handleViewProfile(authData, verifiedXToken, authConfig, profile?.abhaNumber);
            if (fullProfile) {
                profile = fullProfile;
            }
        } catch (e) {
            console.warn('Failed to auto-fetch full profile in OTPVerifier, using extracted profile:', e);
            profileFetchFailed = true;
        }
    }

    if (!profile) {
        // Only show "No ABHA registered" for aadhaar-login if the OTP verification itself succeeded
        // (i.e., we got a valid token) but truly no profile exists.
        // If profile fetch failed due to network/auth issues, show a generic error instead.
        if (authConfig.type === 'aadhaar-login' && verifiedXToken && !profileFetchFailed) {
            throw new Error("NO_ABHA_REGISTERED::No ABHA user registered with this Aadhaar number.");
        }
        throw new Error("Invalid OTP. Please enter a valid OTP.");
    }

    return { xToken: verifiedXToken, profile, isEnrollment: false };
};

export const handleSelectAccountAndFetchProfile = async (
    authData: AuthData,
    transactionId: string,
    selectedAccount: AccountSelection,
    authConfig: AuthConfig,
    tToken?: string
): Promise<{ profile: UserProfile; xToken: string }> => {
    if (!selectedAccount) {
        throw new Error('Please select an ABHA account.');
    }

    let requestBody;
    let endpoint = '/api/abdm/v3/auth/select-account';

    if (authConfig.type === 'communication-mobile') {
        // For communication-mobile, use the profile/login/verify/user endpoint
        endpoint = '/api/abdm/profile/login/verify/user';
        const abhaNumber = selectedAccount.ABHANumber;

        console.log('Communication-mobile account selection:', {
            abhaNumber,
            hasTToken: !!tToken,
            transactionId
        });

        if (!abhaNumber) {
            throw new Error('Selected account missing ABHANumber.');
        }

        if (!tToken) {
            throw new Error('Missing tToken for user verification.');
        }

        requestBody = {
            tenantId: authData.tenantId,
            ABHANumber: abhaNumber,
            txnId: transactionId,
            tToken: tToken
        };
    } else {
        // Standard flows: Use ABHA Number for 'abha-number' flow, otherwise balance address
        const selectionValue = authConfig.type === 'abha-number'
            ? (selectedAccount.ABHANumber || selectedAccount.preferredAbhaAddress)
            : (selectedAccount.preferredAbhaAddress || selectedAccount.ABHANumber);

        requestBody = {
            tenantId: authData.tenantId,
            txnId: transactionId,
            value: selectionValue,
            type: authConfig.type,
            method: authConfig.method
        };
    }

    console.log('Making account selection request to:', endpoint);

    const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authData.token}`
        },
        body: JSON.stringify(requestBody),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || `Account selection failed with status ${response.status}`);
    }

    console.log('Account selection response:', data);

    // Handle different response structures
    let finalXToken: string | undefined;
    let profile: UserProfile | null = null;

    if (authConfig.type === 'communication-mobile') {
        // For communication-mobile, the response has data.data.token
        finalXToken = data.data?.token;

        if (!finalXToken) {
            console.error('No xToken found in account selection response:', data);
            throw new Error("Account selection successful, but xToken is missing in the response.");
        }

        // After account selection, we need to fetch the profile separately using the new xToken
        console.log('Fetching profile with new xToken...');
        profile = await handleViewProfile(authData, finalXToken, authConfig, selectedAccount.ABHANumber);
    } else {
        // For other flows, extract from response
        const dataContainer = data.data || data;
        profile = extractUserProfile(dataContainer);
        finalXToken = dataContainer.tokens?.token || dataContainer.xToken || data.xToken;

        if (!profile || !finalXToken) {
            throw new Error("Account selection successful, but profile/token data is missing in the response.");
        }

        // Auto-fetch full profile if we have a token (for abha-number/abha-address/mobile flows)
        if (finalXToken) {
            try {
                console.log('Auto-fetching full profile in AccountSelector...');
                const fullProfile = await handleViewProfile(authData, finalXToken, authConfig);
                if (fullProfile) {
                    profile = fullProfile;
                }
            } catch (e) {
                console.warn('Failed to auto-fetch full profile in AccountSelector, using partial profile:', e);
            }
        }
    }

    if (!profile) {
        throw new Error("Failed to fetch user profile after account selection.");
    }

    return { profile, xToken: finalXToken };
};

export const getAbhaSuggestions = async (
    authData: AuthData,
    transactionId: string,
    xToken: string
): Promise<string[]> => {
    if (!transactionId) return [];

    const requestBody = {
        txnId: transactionId,
        returnSuggestions: true,
        tenantId: authData.tenantId,
        xToken: xToken // Moved from header to body to fix CORS
    };

    const response = await fetch(`${API_BASE}/api/abdm/v3/enrollment/create`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authData.token}`
            // 'X-Token': xToken // Removed to avoid CORS error
        },
        body: JSON.stringify(requestBody),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch ABHA suggestions');
    }

    return data.suggestions || data.data?.abhaAddressList || [];
};

export const handleCreateABHA = async (
    authData: AuthData,
    transactionId: string,
    selectedAbhaAddress: string,
    xToken: string,
    authConfig: AuthConfig
): Promise<{ profile: UserProfile; xToken: string }> => {
    if (!selectedAbhaAddress || !transactionId || !xToken) {
        throw new Error('Missing required authentication or flow data (Txn ID or X-Token).');
    }

    const requestBody = {
        txnId: transactionId,
        selectedAbhaAddress: selectedAbhaAddress,
        tenantId: authData.tenantId,
        xToken: xToken // Moved from header to body to fix CORS
    };

    const response = await fetch(`${API_BASE}/api/abdm/v3/enrollment/create-from-suggestion`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authData.token}`
            // 'X-Token': xToken // Removed to avoid CORS error
        },
        body: JSON.stringify(requestBody),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'ABHA creation failed');
    }

    // Attempt to extract X-Token from response if provided (some APIs rotate it)
    const newXToken = data.xToken ||
        data.tokens?.token ||
        data.data?.xToken ||
        data.data?.tokens?.token ||
        xToken;

    // Fetch full profile details immediately after creation
    console.log('ABHA Created. Fetching full profile...');
    const profile = await handleViewProfile(authData, newXToken, authConfig, data.healthIdNumber || data.data?.healthIdNumber || data.healthId || data.data?.healthId);

    return { profile, xToken: newXToken };
};

export const handleDownloadABHACard = async (
    authData: AuthData,
    userProfile: UserProfile,
    xToken: string,
    authConfig: AuthConfig,
    action: 'download' | 'view' = 'download'
): Promise<void> => {
    if (!userProfile || !xToken) {
        throw new Error('Authentication data, profile, or xToken is missing. Cannot download card.');
    }

    let contextParam = 'number';
    if (authConfig.type === 'abha-address') {
        contextParam = 'address';
    }

    const abhaNumber = userProfile.abhaNumber || (userProfile as any).ABHANumber;
    const abhaAddress = (userProfile as any).preferredAbhaAddress || userProfile.abhaAddress || '';
    const requestBody = {
        tenantId: authData.tenantId,
        xToken: xToken,
        abhaNumber: abhaNumber || ""
    };

    console.log('[ABDM API] Fetching card with context:', contextParam, 'and abhaNumber:', abhaNumber);

    const url = `${API_BASE}/api/abdm/v3/profile?type=card&context=${contextParam}`;

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authData.token}`,
        },
        body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch ABHA Card. Status: ${response.status}`);
    }

    const data = await response.json();

    // Standardized backend puts card data in data.data.card (wrapped in successResponseNew)
    const rawData = data.data || data;
    const base64Image = typeof rawData === 'string' ? rawData : (rawData.card || rawData.abhaCardPDF || data.abhaCardPDF || data.card);

    if (!base64Image) {
        throw new Error('ABHA Card data (Base64 string) is missing in the response body.');
    }

    // Auto-detect format from base64 header: PNG (iVBOR), PDF (JVBER), JPEG (/9j/)
    let downloadMimeType = 'image/png';
    let fileExtension = 'png';
    if (base64Image.startsWith('/9j/')) {
        downloadMimeType = 'image/jpeg';
        fileExtension = 'jpeg';
    } else if (base64Image.startsWith('JVBER')) {
        downloadMimeType = 'application/pdf';
        fileExtension = 'pdf';
    }

    const imageBlob = b64toBlob(base64Image, downloadMimeType);
    const downloadUrl = window.URL.createObjectURL(imageBlob);

    // Build descriptive filename: ABHA_Card_<number>_<address>.<ext>
    const nameTag = abhaNumber ? abhaNumber.replace(/-/g, '') : (abhaAddress || 'card');
    const fileName = `ABHA_Card_${nameTag}.${fileExtension}`;

    if (action === 'view') {
        window.open(downloadUrl, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 60000);
    } else {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
    }
};

export const handleViewProfile = async (
    authData: AuthData,
    xToken: string,
    authConfig: AuthConfig,
    abhaNumber?: string
): Promise<UserProfile> => {
    if (!xToken) {
        throw new Error('Authentication data or xToken is missing. Cannot view profile.');
    }

    let contextParam = 'number';
    if (authConfig.type === 'abha-address') {
        contextParam = 'address';
    }

    const url = `${API_BASE}/api/abdm/v3/profile?type=profile&context=${contextParam}`;

    console.log('[ABDM API] Viewing profile with context:', contextParam, 'and abhaNumber:', abhaNumber);

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authData.token}`,
        },
        body: JSON.stringify({
            tenantId: authData.tenantId,
            xToken: xToken,
            abhaNumber: abhaNumber || ""
        }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Failed to view ABHA Profile');
    }

    const profile = extractUserProfile(data.data || data);
    if (!profile) {
        throw new Error("Profile retrieval successful, but data is incomplete.");
    }

    return profile;
};

export const handleLinkAbhaAddress = async (
    authData: AuthData,
    userProfile: UserProfile
): Promise<{ memberId: string; isUpdate: boolean; fullName: string }> => {
    if (!authData || !userProfile) {
        throw new Error('Missing auth data or user profile to link.');
    }

    // Helper: convert undefined/null/"undefined"/"null" to empty string
    const safe = (val: string | undefined | null): string => {
        if (val === undefined || val === null) return '';
        const trimmed = String(val).trim();
        if (trimmed.toLowerCase() === 'undefined' || trimmed.toLowerCase() === 'null' || trimmed === 'N/A') return '';
        return trimmed;
    };

    const fullName = safe(userProfile.fullName) || safe((userProfile as any)?.name);
    const abhaNumber = safe(userProfile.abhaNumber) || safe((userProfile as any)?.ABHANumber);

    // Validate: must have at least a name or ABHA number
    if (!fullName && !abhaNumber) {
        throw new Error('Cannot register: patient has no name and no ABHA number');
    }

    const requestBody = {
        abhaNumber: abhaNumber,
        abhaAddress: safe(userProfile.abhaAddress) || safe((userProfile as any)?.preferredAbhaAddress),
        fullName: fullName,
        name: fullName, // Map to backend 'name' field
        firstName: fullName.split(' ')[0] || '',
        lastName: fullName.split(' ').slice(1).join(' ') || '',
        dob: safe(userProfile.dob),
        gender: safe(userProfile.gender),
        address: safe(userProfile.address),
        town: safe(userProfile.town),
        district: safe(userProfile.district),
        state: safe(userProfile.state),
        pincode: safe(userProfile.pincode),
        mobile: safe(userProfile.mobile),
        email: safe(userProfile.email),
        photoBase64: safe(userProfile.profilePhoto),
        healthIdNumber: abhaNumber,
        healthId: safe(userProfile.abhaAddress) || safe((userProfile as any)?.preferredAbhaAddress),
        abhaAddresses: userProfile.abhaAddresses || (safe(userProfile.abhaAddress) ? [safe(userProfile.abhaAddress)] : []),
        tenantId: safe(authData.tenantId),
    };

    console.log('[Registration] Sending payload:', { name: fullName, abhaNumber, mobile: requestBody.mobile, tenantId: requestBody.tenantId });

    // Use dynamic callbackUrl from tenant config (set at login)
    const callbackUrl = authData.callbackUrl;
    if (!callbackUrl) {
        console.warn('[Registration] No callbackUrl configured for this tenant, skipping client-side callback');
        return { memberId: '', isUpdate: false, fullName: fullName };
    }

    console.log('[Registration] Calling callback:', callbackUrl);
    const response = await fetch(callbackUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authData.token}`
        },
        body: JSON.stringify(requestBody),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || 'Failed to register member');
    }

    if (data.success === false) {
        throw new Error(data.message || 'Registration returned unsuccessful status');
    }

    console.log('[Registration] Success:', data);

    return {
        memberId: data.data?.memberId || data.data?.id || '',
        isUpdate: data.isUpdate || false,
        fullName: data.data?.fullName || fullName,
    };
};

// ═══════════════════════════════════════════════════════════════
// M1 CERTIFICATION — EXTENDED API FUNCTIONS
// ═══════════════════════════════════════════════════════════════

// 1. FIND ABHA — Search by ABHA Number/Address
export const handleSearchAbha = async (
    authData: AuthData,
    searchParams: { abhaNumber?: string; abhaAddress?: string }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
    const response = await fetch(`${API_BASE}/api/abdm/v3/abha/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify({ ...searchParams, tenantId: authData.tenantId }),
    });
    const data = await response.json();
    if (!response.ok || data.status === false || data.status === 'error') {
        const msg: string = data.message || data.details || '';
        if (msg.toLowerCase().includes('user not found') || msg.includes('ABDM-1114')) {
            throw new Error('User not found');
        }
        throw new Error(msg || 'ABHA search failed');
    }
    return data.data || data;
};

// ═══════════════════════════════════════════════════════════════
// FIND ABHA BY MOBILE — Search ABHA IDs linked to a mobile
// ═══════════════════════════════════════════════════════════════

/** One ABHA profile returned by the search */
export interface AbhaProfileItem {
    index: number;
    ABHANumber: string;
    name: string;
    gender: string;
    kycVerified: string;
    authMethods: string[];
}

export interface AbhaSearchResult {
    /** Transaction ID — reuse in the index-based OTP request */
    txnId: string;
    /** List of ABHA profiles found for the mobile */
    abhaList: AbhaProfileItem[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    raw?: any;
}

export const handleSearchAbhaByMobile = async (
    authData: AuthData,
    mobile: string
): Promise<AbhaSearchResult> => {
    if (!mobile || !/^\d{10}$/.test(mobile)) {
        throw new Error('Please enter a valid 10-digit mobile number.');
    }

    const response = await fetch(`${API_BASE}/api/abdm/v3/profile/account/abha/search`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authData.token}`,
        },
        body: JSON.stringify({ tenantId: authData.tenantId, mobile }),
    });

    let data: any;
    try {
        data = await response.json();
    } catch {
        throw new Error('Server unavailable. Please check your connection and try again.');
    }

    if (!response.ok || data.status === false) {
        // Map ABDM-1114 "User not found" to a friendly message
        // Check all nested fields — error details may be in data.error, data.abdmError, or data.message
        const allText: string = JSON.stringify(data).toLowerCase();
        if (allText.includes('user not found') || allText.includes('abdm-1114')) {
            throw new Error('USER_NOT_FOUND');
        }
        throw new Error(data.message || `Search failed (HTTP ${response.status})`);
    }

    // Real ABDM response shape:
    // { status: true, data: [{ txnId: "...", ABHA: [{ index, ABHANumber, name, gender, ... }] }] }
    const inner = Array.isArray(data.data) ? data.data[0] : (data.data || data);
    const txnId: string = inner?.txnId || inner?.transactionId || '';
    const rawList: AbhaProfileItem[] = inner?.ABHA || inner?.abhaIdList || [];

    if (!rawList.length) {
        throw new Error('No ABHA ID is linked to this mobile number. Please check the number and try again.');
    }

    return { txnId, abhaList: rawList, raw: inner };
};

/** Request OTP for a selected ABHA by its 1-based index from the search result.
 *  Uses the unified /api/abdm/v3/auth/request/otp endpoint with type="search-abha".
 */
export const handleSearchAbhaRequestOtp = async (
    authData: AuthData,
    txnId: string,
    index: number
): Promise<{ txnId: string; maskedMobile?: string }> => {
    const response = await fetch(`${API_BASE}/api/abdm/v3/auth/request/otp`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authData.token}`,
        },
        body: JSON.stringify({
            tenantId: authData.tenantId,
            type: 'search-abha',
            method: 'index',
            value: String(index),   // e.g. "1" or "2"
            txnId,                  // txnId from the search response — mandatory
        }),
    });

    const data = await response.json();

    if (!response.ok || data.status === false) {
        throw new Error(extractAbdmError(data, response.status));
    }

    const inner = data.data || data;

    // Resolve the new txnId (ABDM may return the same or an updated one)
    const newTxnId: string = inner?.txnId || data?.txnId || txnId;

    // Extract masked mobile from message, e.g. "OTP sent to ******2159"
    let maskedMobile = '';
    const msgs = [inner?.message, data?.message].filter(Boolean) as string[];
    for (const msg of msgs) {
        const match = msg.match(/(\*+\d+)/);
        if (match) { maskedMobile = match[1]; break; }
    }

    return { txnId: newTxnId, maskedMobile };
};

// ── Face Auth (QR-based) APIs ────────────────────────────────────────────────

/** Init face auth transaction → returns { txnId, qrUrl } */
export const handleFaceAuthInit = async (
    authData: AuthData,
    tenantId: string
): Promise<{ txnId: string; qrUrl: string }> => {
    const response = await fetch(`${API_BASE}/api/abdm/v3/face-auth/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify({ tenantId }),
    });
    const data = await response.json();
    if (!response.ok || data.status === false) throw new Error(extractAbdmError(data, response.status));
    const inner = data.data || data;
    return { txnId: inner.txnId, qrUrl: inner.qrUrl };
};

/** Poll capturePID status: PENDING | VERIFIED | COMPLETE | FAILED */
export const handleFaceAuthPoll = async (
    authData: AuthData,
    tenantId: string,
    txnId: string
): Promise<{ status: string; message?: string; txnId?: string }> => {
    const response = await fetch(`${API_BASE}/api/abdm/v3/face-auth/poll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify({ tenantId, txnId }),
    });
    const data = await response.json();
    if (!response.ok || data.status === false) throw new Error(extractAbdmError(data, response.status));
    const inner = data.data || data;
    return { status: inner.status || inner.authResult || 'PENDING', message: inner.message, txnId: inner.txnId };
};

/** Enroll (create ABHA) using face auth QR flow */
export const handleFaceAuthEnroll = async (
    authData: AuthData,
    tenantId: string,
    txnId: string,
    aadhaar: string,
    mobile: string
): Promise<any> => {
    const response = await fetch(`${API_BASE}/api/abdm/v3/face-auth/enroll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify({ tenantId, txnId, aadhaar, mobile }),
    });
    const data = await response.json();
    if (!response.ok || data.status === false) throw new Error(extractAbdmError(data, response.status));
    return data.data || data;
};

/** Select ABHA from search results for face auth login → returns { txnId, qrUrl } */
export const handleFaceAuthSearchSelect = async (
    authData: AuthData,
    tenantId: string,
    txnId: string,
    index: string
): Promise<{ txnId: string; qrUrl: string }> => {
    const response = await fetch(`${API_BASE}/api/abdm/v3/face-auth/search-select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify({ tenantId, txnId, index }),
    });
    const data = await response.json();
    if (!response.ok || data.status === false) throw new Error(extractAbdmError(data, response.status));
    const inner = data.data || data;
    return { txnId: inner.txnId, qrUrl: inner.qrUrl };
};

/** Verify face auth login → returns profile + tokens */
export const handleFaceAuthLoginVerify = async (
    authData: AuthData,
    tenantId: string,
    txnId: string
): Promise<any> => {
    const response = await fetch(`${API_BASE}/api/abdm/v3/face-auth/login-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify({ tenantId, txnId }),
    });
    const data = await response.json();
    if (!response.ok || data.status === false) throw new Error(extractAbdmError(data, response.status));
    return data.data || data;
};

// 2. Password Login
export const handlePasswordLogin = async (
    authData: AuthData,
    abhaId: string,
    password: string,
    loginHint: string = 'abha-number'
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
    const response = await fetch(`${API_BASE}/api/abdm/v3/auth/login/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify({ abhaId, password, loginHint, tenantId: authData.tenantId }),
    });
    const data = await response.json();
    if (!response.ok || data.status === 'error') {
        throw new Error(data.message || data.details || 'Password login failed');
    }
    return data.data || data;
};

// 10a. Forgot ABHA — Request OTP
export const handleForgotAbhaRequestOtp = async (
    authData: AuthData,
    method: string,
    value: string,
    scopeType: string = 'forgot-abha'
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
    const response = await fetch(`${API_BASE}/api/abdm/v3/forgot-abha/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify({ method, value, scopeType, tenantId: authData.tenantId }),
    });
    const data = await response.json();
    if (!response.ok || data.status === 'error') {
        throw new Error(data.message || data.details || 'Forgot ABHA OTP failed');
    }
    return data.data || data;
};

// 10b. Forgot ABHA — Verify OTP
export const handleForgotAbhaVerifyOtp = async (
    authData: AuthData,
    method: string,
    txnId: string,
    otp: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
    const response = await fetch(`${API_BASE}/api/abdm/v3/forgot-abha/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify({ method, txnId, otp, tenantId: authData.tenantId }),
    });
    const data = await response.json();
    if (!response.ok || data.status === 'error') {
        throw new Error(data.message || data.details || 'Forgot ABHA verify failed');
    }
    return data.data || data;
};

// 4a. Profile Account — Request OTP
export const handleProfileAccountRequestOtp = async (
    authData: AuthData,
    xToken: string,
    action: string,
    method: string = 'aadhaar',
    value: string = ''
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
    const response = await fetch(`${API_BASE}/api/abdm/v3/profile/account/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify({ xToken, action, method, value, tenantId: authData.tenantId }),
    });
    const data = await response.json();
    if (!response.ok || data.status === 'error') {
        throw new Error(data.message || data.details || 'Profile OTP request failed');
    }
    return data.data || data;
};

// 4b. Profile Account — Verify
export const handleProfileAccountVerify = async (
    authData: AuthData,
    xToken: string,
    action: string,
    params: { method?: string; txnId?: string; otp?: string; password?: string; newValue?: string; reason?: string }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
    const response = await fetch(`${API_BASE}/api/abdm/v3/profile/account/verify-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify({ xToken, action, ...params, tenantId: authData.tenantId }),
    });
    const data = await response.json();
    if (!response.ok || data.status === 'error') {
        throw new Error(data.message || data.details || 'Profile verify failed');
    }
    return data.data || data;
};

// 5. QR Code
export const handleGetQrCode = async (
    authData: AuthData,
    xToken: string
): Promise<string> => {
    const response = await fetch(`${API_BASE}/api/abdm/v3/profile/qr-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify({ xToken, tenantId: authData.tenantId }),
    });
    const data = await response.json();
    if (!response.ok || data.status === 'error') {
        throw new Error(data.message || data.details || 'QR Code fetch failed');
    }
    return data.data?.qrCode || '';
};

// 6. Update Profile (photo, name)
export const handleUpdateProfile = async (
    authData: AuthData,
    xToken: string,
    updates: { photo?: string; firstName?: string; middleName?: string; lastName?: string; email?: string }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
    const response = await fetch(`${API_BASE}/api/abdm/v3/profile/account/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify({ xToken, ...updates, tenantId: authData.tenantId }),
    });
    const data = await response.json();
    if (!response.ok || data.status === 'error') {
        throw new Error(data.message || data.details || 'Profile update failed');
    }
    return data.data || data;
};

// 11a. Logout
export const handleLogoutProfile = async (
    authData: AuthData,
    xToken: string
): Promise<void> => {
    const response = await fetch(`${API_BASE}/api/abdm/v3/profile/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify({ xToken, tenantId: authData.tenantId }),
    });
    const data = await response.json();
    if (!response.ok || data.status === 'error') {
        throw new Error(data.message || data.details || 'Logout failed');
    }
};

// 11b. Refresh Token
export const handleRefreshToken = async (
    authData: AuthData,
    xToken: string
): Promise<string> => {
    const response = await fetch(`${API_BASE}/api/abdm/v3/profile/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData.token}` },
        body: JSON.stringify({ xToken, tenantId: authData.tenantId }),
    });
    const data = await response.json();
    if (!response.ok || data.status === 'error') {
        throw new Error(data.message || data.details || 'Token refresh failed');
    }
    return data.data?.token || data.data?.xToken || xToken;
};
